import React, { useState } from "react";
import {
    Box, Button, Modal, ModalOverlay, ModalContent, ModalHeader, ModalCloseButton, ModalBody,
    ModalFooter, Tabs, TabList, Tab,
} from "@chakra-ui/react";
import CreateFreeForm, {
    buildFreeInitialState,
} from "../../components/Main/auditorAndArtiste/subscriptions/CreateFreeForm";
import CreatePaidForm, {
    buildPaidInitialState,
} from "../../components/Main/auditorAndArtiste/subscriptions/CreatePaidForm";
import PaidSuccessPanel from "../../components/Main/auditorAndArtiste/subscriptions/PaidSuccessPanel";
import OwnerPicker, { buildOwnerInitialState } from "./OwnerPicker";
import { handleCreateSubmit, useResetOnOpen } from "./createSubscriptionHelpers";

const MODES = ["free", "paid"];

/**
 * Tab switcher between Gratuit and Payant modes.
 * @param {Object} props - { mode, onChange }
 * @returns {React.ReactElement}
 */
function ModeTabs({ mode, onChange }) {
    return (
        <Tabs index={MODES.indexOf(mode)} onChange={(i) => onChange(MODES[i])} variant="soft-rounded" colorScheme="brand" mb="16px">
            <TabList gap="8px">
                <Tab flex="1" fontSize="0.85rem">Gratuit</Tab>
                <Tab flex="1" fontSize="0.85rem">Payant</Tab>
            </TabList>
        </Tabs>
    );
}

/**
 * Renders the dialog body: owner picker + tabbed form, or the paid success panel.
 * @param {Object} props - Dialog body props
 * @returns {React.ReactElement}
 */
function DialogBody({ mode, setMode, owner, setOwner, freeForm, setFreeForm, paidForm, setPaidForm, paidResult }) {
    if (paidResult) return <PaidSuccessPanel result={paidResult} />;
    return (
        <Box>
            <OwnerPicker owner={owner} setOwner={setOwner} />
            <ModeTabs mode={mode} onChange={setMode} />
            {mode === "free"
                ? <CreateFreeForm form={freeForm} setForm={setFreeForm} />
                : <CreatePaidForm form={paidForm} setForm={setPaidForm} />}
        </Box>
    );
}

/**
 * Renders the dialog footer (Annuler/Créer or just Fermer in the paid success state).
 * @param {Object} props - Footer props
 * @returns {React.ReactElement}
 */
function DialogFooterRow({ paidResult, loading, onClose, onSubmit }) {
    if (paidResult) return <Button colorScheme="brand" onClick={onClose}>Fermer</Button>;
    return (
        <>
            <Button variant="ghost" onClick={onClose}>Annuler</Button>
            <Button colorScheme="brand" onClick={onSubmit} isLoading={loading} loadingText="Création...">Créer</Button>
        </>
    );
}

/**
 * Modal to create a free or paid subscription for any owner (user, artist_group, entity).
 * Free creation is immediate; paid creation returns a setup_link to forward to the owner.
 * @param {Object} props
 * @param {boolean} props.open - Dialog open state
 * @param {Function} props.onClose - Close callback
 * @param {Function} props.onCreated - Called after a successful creation (parent refresh)
 * @returns {React.ReactElement}
 */
function CreateGlobalSubscriptionDialog({ open, onClose, onCreated }) {
    const [mode, setMode] = useState("free");
    const [owner, setOwner] = useState(buildOwnerInitialState());
    const [freeForm, setFreeForm] = useState(buildFreeInitialState());
    const [paidForm, setPaidForm] = useState(buildPaidInitialState());
    const [paidResult, setPaidResult] = useState(null);
    const [loading, setLoading] = useState(false);

    useResetOnOpen({ open, setMode, setOwner, setFreeForm, setPaidForm, setPaidResult });

    const onSubmit = () => handleCreateSubmit({
        mode, owner, freeForm, paidForm, setLoading, setPaidResult, onCreated, onClose,
    });

    return (
        <Modal isOpen={open} onClose={onClose} size="lg" isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>Nouvel abonnement</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <DialogBody mode={mode} setMode={setMode} owner={owner} setOwner={setOwner}
                        freeForm={freeForm} setFreeForm={setFreeForm} paidForm={paidForm} setPaidForm={setPaidForm}
                        paidResult={paidResult} />
                </ModalBody>
                <ModalFooter gap="8px">
                    <DialogFooterRow paidResult={paidResult} loading={loading} onClose={onClose} onSubmit={onSubmit} />
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
}

export default CreateGlobalSubscriptionDialog;
